import { fetchWithAuth } from './auth';

export interface NotepadEntry {
  id: string;
  job_id: string;
  user_id: string;
  entry_text: string;
  photo_ref_sequence: number | null;
  created_at: string;
}

export interface CreateNotepadEntryArgs {
  job_id: string;
  entry_text: string;
  photo_ref_sequence?: number | null;
}

export async function createNotepadEntry(
  args: CreateNotepadEntryArgs,
): Promise<NotepadEntry> {
  const r = await fetchWithAuth('/api/field/notepad/entries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!r.ok) {
    throw new Error(
      `createNotepadEntry: ${r.status}: ${(await r.text()).slice(0, 200)}`,
    );
  }
  return (await r.json()) as NotepadEntry;
}

export async function listNotepadEntries(jobId: string): Promise<NotepadEntry[]> {
  const r = await fetchWithAuth(
    `/api/field/notepad/entries?job_id=${encodeURIComponent(jobId)}`,
  );
  if (!r.ok) throw new Error(`listNotepadEntries: ${r.status}`);
  return (await r.json()) as NotepadEntry[];
}

export interface InspectionSummary {
  id: string;
  job_id: string;
  ai_draft: string | null;
  raw_notes_snapshot: string | null;
  edited_draft: string | null;
  posted_to_eva_at: string | null;
  created_at: string;
}

export async function getLatestSummary(jobId: string): Promise<InspectionSummary | null> {
  const r = await fetchWithAuth(
    `/api/field/summary?job_id=${encodeURIComponent(jobId)}`,
  );
  if (!r.ok) throw new Error(`getLatestSummary: ${r.status}`);
  const data = await r.json();
  return (data ?? null) as InspectionSummary | null;
}

export async function generateSummary(jobId: string): Promise<InspectionSummary> {
  const r = await fetchWithAuth('/api/field/summary/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ job_id: jobId }),
  });
  if (!r.ok) {
    throw new Error(
      `generateSummary: ${r.status}: ${(await r.text()).slice(0, 200)}`,
    );
  }
  return (await r.json()) as InspectionSummary;
}

export interface PostToEvaResult {
  ok: true;
  summary_id: string;
  posted_to_eva_at: string;
  draft_note_id: string;
  raw_note_id: string;
}

export async function postSummaryToEva(
  summaryId: string,
  editedDraft: string,
): Promise<PostToEvaResult> {
  const r = await fetchWithAuth('/api/field/summary/post-to-eva', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ summary_id: summaryId, edited_draft: editedDraft }),
  });
  if (!r.ok) {
    throw new Error(
      `postSummaryToEva: ${r.status}: ${(await r.text()).slice(0, 200)}`,
    );
  }
  return (await r.json()) as PostToEvaResult;
}
